// ROOT: frontend/src/components/Hero.jsx

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Star, Zap, ShieldCheck } from 'lucide-react';
import { api } from '../api';

const Hero = () => {
  const [brandName, setBrandName] = useState('IGGrowth');

  const [homepageTitle, setHomepageTitle] = useState(
    'Grow Your Instagram & TikTok with Organic AI Growth'
  );

  const [homepageSubtitle, setHomepageSubtitle] = useState(
    'No bots, no fake followers, no passwords. Gain real targeted followers automatically using AI, human experts and our patent-pending technology.'
  );

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settings = await api.getPublicSettings();

        const name =
          settings?.brandName ||
          settings?.brand_name ||
          'IGGrowth';

        setBrandName(name);

        setHomepageTitle(
          settings?.homepageTitle ||
          settings?.homepage_title ||
          'Grow Your Instagram & TikTok with Organic AI Growth'
        );

        setHomepageSubtitle(
          settings?.homepageSubtitle ||
          settings?.homepage_subtitle ||
          'No bots, no fake followers, no passwords. Gain real targeted followers automatically using AI, human experts and our patent-pending technology.'
        );
      } catch (error) {
        console.error('Failed to load homepage settings:', error);
      }
    };

    loadSettings();
  }, []);

  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-32 -left-32 w-96 h-96 bg-blue-100/50 rounded-full blur-3xl" />
        <div className="absolute -top-20 right-0 w-96 h-96 bg-pink-100/50 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-16 pb-20 lg:pt-24 lg:pb-28">
        <div className="grid lg:grid-cols-2 gap-14 items-center">

          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-4 py-2 text-xs font-bold text-slate-700">
              <Zap className="w-4 h-4" />
              AI-Powered Organic Growth
            </div>

            <h1 className="mt-6 text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tight text-[#0a1930] leading-[1.02]">
              {homepageTitle}
            </h1>

            <p className="mt-6 text-lg md:text-xl leading-8 text-slate-600 max-w-xl">
              {homepageSubtitle}
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Link
                to="/get-started"
                className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-[#0a1930] text-white font-bold hover:opacity-90 transition-opacity"
              >
                Get Started
                <ChevronRight className="w-5 h-5" />
              </Link>

              <a
                href="#pricing"
                className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full border border-slate-200 bg-white text-[#0a1930] font-bold hover:bg-slate-50 transition-colors"
              >
                View Pricing
              </a>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-slate-500">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-slate-700" />
                No password required
              </div>

              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 text-slate-700" />
                Organic growth
              </div>

              <div className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-slate-700" />
                AI powered
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="relative mx-auto max-w-lg">
              <div className="absolute -inset-4 rounded-[2rem] bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-pink-500/20 blur-2xl" />

              <div className="relative rounded-[2rem] border border-slate-200 bg-white shadow-2xl overflow-hidden">
                <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-bold text-[#0a1930]">
                      {brandName} Growth
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      AI-powered dashboard
                    </p>
                  </div>

                  <div className="w-3 h-3 rounded-full bg-green-400" />
                </div>

                <div className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-slate-500">
                        Followers
                      </p>
                      <p className="mt-1 text-3xl font-extrabold text-[#0a1930]">
                        24,680
                      </p>
                    </div>

                    <div className="rounded-xl bg-slate-50 px-4 py-3 text-right">
                      <p className="text-xs text-slate-500">
                        This month
                      </p>
                      <p className="text-sm font-extrabold text-green-600">
                        +2,840
                      </p>
                    </div>
                  </div>

                  <div className="mt-7 h-32 flex items-end gap-2">
                    {[32, 45, 38, 58, 52, 72, 66, 84, 76, 96, 88, 100].map(
                      (height, index) => (
                        <div
                          key={index}
                          className="flex-1 rounded-t-lg bg-gradient-to-t from-blue-500 to-pink-500"
                          style={{ height: `${height}%` }}
                        />
                      )
                    )}
                  </div>

                  <div className="mt-7 grid grid-cols-3 gap-3">
                    <div className="rounded-xl bg-slate-50 p-4">
                      <p className="text-xs text-slate-500">
                        Reach
                      </p>
                      <p className="mt-1 font-extrabold text-[#0a1930]">
                        128K
                      </p>
                    </div>

                    <div className="rounded-xl bg-slate-50 p-4">
                      <p className="text-xs text-slate-500">
                        Engagement
                      </p>
                      <p className="mt-1 font-extrabold text-[#0a1930]">
                        8.7%
                      </p>
                    </div>

                    <div className="rounded-xl bg-slate-50 p-4">
                      <p className="text-xs text-slate-500">
                        Growth
                      </p>
                      <p className="mt-1 font-extrabold text-[#0a1930]">
                        +13.2%
                      </p>
                    </div>
                  </div>

                  <div className="mt-6 rounded-xl border border-slate-100 p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-slate-500">
                        AI Growth Engine
                      </span>
                      <span className="text-xs font-bold text-green-600">
                        Active
                      </span>
                    </div>

                    <div className="mt-3 h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full w-[82%] rounded-full bg-gradient-to-r from-blue-500 to-pink-500" />
                    </div>
                  </div>
                </div>

                <div className="px-6 py-4 border-t border-slate-100 bg-slate-50">
                  <span className="text-xs text-slate-500 font-semibold">
                    {brandName}/dashboard
                  </span>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default Hero;
